<!-- ................................................................................ -->
<!-- https://demos.creative-tim.com/argon-dashboard-pro/pages/dashboards/landing.html -->

<template>
  <div id="app">
    <router-view class="..." />

    <div v-if="showInstallPrompt" class="pwa-install-backdrop">
      <div class="pwa-install-card">
        <button type="button" class="pwa-install-close" aria-label="ปิด" @click="closeInstallPrompt">
          &times;
        </button>

        <img :src="pwaIcon" class="pwa-install-icon" alt="TNJTEK">
        <h5 class="pwa-install-title">ติดตั้ง TNJTEK Member</h5>
        <p class="pwa-install-description">
          เพิ่มแอปไว้บนหน้าจอหลัก เพื่อเข้าใช้งานได้สะดวกยิ่งขึ้น
        </p>

        <div v-if="!isSecurePWAContext" class="pwa-install-warning">
          การติดตั้งแอปต้องเปิดเว็บไซต์ผ่าน HTTPS
        </div>

        <div v-else-if="isIOS" class="pwa-ios-help">
          แตะปุ่ม Share แล้วเลือก “Add to Home Screen”
        </div>

        <button type="button" class="btn btn-primary w-100 mb-0" @click="installPWA">
          {{ installButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const APP_BACKGROUND_AT_KEY = 'tnjtek_app_background_at';
const APP_REFRESH_AFTER_MS = 30 * 60 * 1000;

export default {
  name: 'App',
  data() {
    return {
      deferredInstallPrompt: null,
      installPromptReady: false,
      iosHelpVisible: false,
      insecureContextHelpVisible: false,
      pwaIcon: `${process.env.BASE_URL}icons/icon-192.png?v=1`,
    }
  },
  computed: {
    isIOS() {
      return /iphone|ipad|ipod/i.test(navigator.userAgent);
    },
    isMobile() {
      return window.matchMedia('(max-width: 767px)').matches
        || /android|iphone|ipad|ipod/i.test(navigator.userAgent);
    },
    isInstalled() {
      return window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
    },
    isSecurePWAContext() {
      return window.isSecureContext
        || ['localhost', '127.0.0.1'].includes(window.location.hostname);
    },
    installButtonText() {
      if (!this.isSecurePWAContext) return 'ต้องเปิดผ่าน HTTPS';
      return this.isIOS ? 'ดูวิธีติดตั้ง' : 'ติดตั้งแอป';
    },
    showInstallPrompt() {
      if (!this.isMobile || this.isInstalled) return false;
      return this.installPromptReady
        || this.iosHelpVisible
        || this.insecureContextHelpVisible;
    },
  },
  beforeCreate() {
    if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
      console.log("%c It's a local server! ", 'background: #222; color: #bada55');
    } else {
      // if (location.protocol !== 'https:') { /* redirect to https */
      //   location.replace(`https:${location.href.substring(location.protocol.length)}`);
      // }
    }
  },
  mounted() {
    window.addEventListener('beforeinstallprompt', this.captureInstallPrompt);
    window.addEventListener('appinstalled', this.handleAppInstalled);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('pagehide', this.rememberBackgroundTime);

    if (this.isIOS && !this.isInstalled) {
      this.iosHelpVisible = true;
    }

    if (!this.isSecurePWAContext && !this.isInstalled) {
      this.insecureContextHelpVisible = true;
    }
  },
  beforeDestroy() {
    window.removeEventListener('beforeinstallprompt', this.captureInstallPrompt);
    window.removeEventListener('appinstalled', this.handleAppInstalled);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pagehide', this.rememberBackgroundTime);
  },
  methods: {
    rememberBackgroundTime() {
      sessionStorage.setItem(APP_BACKGROUND_AT_KEY, Date.now().toString());
    },
    handleVisibilityChange() {
      if (document.hidden) {
        this.rememberBackgroundTime();
        return;
      }

      const backgroundAt = Number(sessionStorage.getItem(APP_BACKGROUND_AT_KEY));
      sessionStorage.removeItem(APP_BACKGROUND_AT_KEY);

      if (backgroundAt && Date.now() - backgroundAt >= APP_REFRESH_AFTER_MS) {
        window.location.reload();
      }
    },
    captureInstallPrompt(event) {
      event.preventDefault();
      this.deferredInstallPrompt = event;
      this.installPromptReady = true;
    },
    async installPWA() {
      if (!this.isSecurePWAContext) {
        this.$swal({
          title: 'ต้องใช้ HTTPS',
          text: `Chrome ไม่อนุญาตให้ติดตั้ง PWA จาก ${window.location.origin} กรุณาเปิดเว็บไซต์ผ่าน HTTPS`,
          icon: 'warning',
        });
        return;
      }

      if (this.isIOS) {
        this.$swal({
          title: 'ติดตั้งบน iPhone / iPad',
          text: 'แตะปุ่ม Share ใน Safari จากนั้นเลือก Add to Home Screen และกด Add',
          icon: 'info',
        });
        return;
      }

      if (!this.deferredInstallPrompt) return;

      this.deferredInstallPrompt.prompt();
      await this.deferredInstallPrompt.userChoice;
      this.deferredInstallPrompt = null;
      this.installPromptReady = false;
    },
    closeInstallPrompt() {
      this.installPromptReady = false;
      this.iosHelpVisible = false;
      this.insecureContextHelpVisible = false;
    },
    handleAppInstalled() {
      this.deferredInstallPrompt = null;
      this.installPromptReady = false;
      this.iosHelpVisible = false;
      this.insecureContextHelpVisible = false;
      localStorage.setItem('tnjtek_show_notification_guide', 'installed');
      window.dispatchEvent(new CustomEvent('tnjtek:pwa-installed'));
    },
  },
}
</script>

<style>
@import './has-tooltip.css';
@import url(//fonts.googleapis.com/css2?family=Prompt:wght@300&display=swap);

#app {
  font-family: 'Prompt', sans-serif !important;
}

.pwa-install-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px;
  background: rgba(23, 43, 77, 0.55);
}

.pwa-install-card {
  position: relative;
  width: 100%;
  max-width: 420px;
  padding: 24px;
  text-align: center;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 18px 45px rgba(23, 43, 77, 0.22);
}

.pwa-install-close {
  position: absolute;
  top: 8px;
  right: 12px;
  padding: 4px 8px;
  color: #8898aa;
  font-size: 28px;
  line-height: 1;
  background: transparent;
  border: 0;
}

.pwa-install-icon {
  width: 72px;
  height: 72px;
  margin-bottom: 12px;
  border-radius: 16px;
}

.pwa-install-title {
  margin-bottom: 8px;
  color: #172b4d;
  font-weight: 600;
}

.pwa-install-description,
.pwa-ios-help,
.pwa-install-warning {
  color: #67748e;
}

.pwa-install-description {
  margin-bottom: 18px;
}

.pwa-ios-help,
.pwa-install-warning {
  margin-bottom: 16px;
  padding: 10px;
  background: #f6f9fc;
  border-radius: 10px;
  font-size: 14px;
}

.pwa-install-warning {
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.card .card-body {
  font-family: 'Prompt', sans-serif !important;
}

.modal.show {
  display: block !important;
}

.w-100 {
  width: 100% !important;
}

.text-center {
  align-items: center !important;
}

.cursor_pointer {
  cursor: pointer !important;
}

.swal-text {
  text-align: center !important;
}

.swal-footer {
  text-align: center !important;
}

.float-right {
  float: right !important;
}

.show-mobile {
  display: none !important;
}

@media screen and (max-width: 767px) {

  /* (max-width: 600px) */
  .hidden-mobile {
    display: none !important;
  }

  .show-mobile {
    display: block !important;
  }
}

/* ................................................. */
.transform-none {
  text-transform: none !important;
}

.cut-text {
  text-overflow: ellipsis !important;
  overflow: hidden !important;
  white-space: nowrap !important;
}

.photo-album {
  width: 100%;
  height: 100%;
  object-fit: cover !important;
  /* height: 175px !important; */
}

.photo-cover {
  width: 100%;
  height: 375px !important;
  object-fit: cover !important;
}

@media screen and (max-width: 992px) {

  /* (max-width: 767px) */
  .photo-cover {
    height: 275px !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fade {
    transition: none;
  }
}

.avatar-3xl {
  width: 245px !important;
  height: 245px !important;
}

.blur {
  background-color: rgba(245, 245, 245, 1);
  opacity: .05;
}

.dropdown-menu-right {
  right: 0;
  left: auto
}

.dropup-right .dropdown-menu:after {
  left: auto !important;
  right: 10px !important;
}

@media (min-width: 576px) {
  .modal-lgx {
    max-width: 600px
  }
}

.bg-error {
  background: #ffed00 !important;
  color: black;
}
</style>
